import { getItem, setItem, removeItem } from '../storage/scopedStorage'

/**
 * Feature flags (T-029).
 *
 * There is exactly one flag here and it exists for exactly three weeks
 * (DEVELOPMENT_PLAN.md §9). This is not a feature-flag platform and must not
 * grow into one — when `newCounter` is removed, this file goes with it.
 *
 * WHY A FLAG AT ALL, FOR A SCREEN REWRITE
 *
 * The sales order screen writes to the real books. There is no staging
 * environment (plan §10.1), so the first real exercise the new screen gets is a
 * salesperson serving a real customer. When that goes wrong, the recovery has to
 * be available to THEM, in the moment, with someone waiting at the counter — not
 * to a developer who can ship a revert in twenty minutes.
 *
 * So the fallback is a click, not a deploy.
 *
 * TWO LAYERS, AND WHY BOTH
 *
 *   Build-time default — REACT_APP_FEATURE_NEW_COUNTER. This is how the default
 *   flips for everyone in week 2, and how it flips BACK for everyone if week 1
 *   goes badly. It ships in the bundle; it is not a secret and nothing here
 *   should ever be.
 *
 *   Runtime override — per shop and per user, stored through T-020's scoped
 *   storage. This is how one salesperson opts in during week 1 while the rest of
 *   the shop carries on, and how they opt back out mid-sale. Scoped because the
 *   counter PC is shared: the morning shift's preference must not decide the
 *   evening shift's screen, and one shop's must never reach another's.
 *
 * The override wins when it is set. Absent an override, the build default
 * applies. Clearing the override returns to the default rather than to `false` —
 * "I have no opinion" and "I want the old one" are different states, and
 * collapsing them would mean the week-2 default flip silently skipped everyone
 * who had ever touched the switch.
 */

export const FLAGS = {
    /**
     * The rewritten counter screen (Sprint 3). Remove this flag, this file and
     * the old SalesOrderCreate.jsx together in week 3 — not one before the
     * others, or the tree is left with a dead branch nobody dares delete.
     */
    NEW_COUNTER: 'newCounter',
}

/**
 * CRA inlines REACT_APP_* at BUILD time, so this is read once and cannot change
 * without a rebuild. Anything but the literal string 'true' is off: a flag that
 * turns itself on because someone set it to 'false' would be the worst possible
 * failure of a rollback switch.
 */
const BUILD_DEFAULTS = {
    [FLAGS.NEW_COUNTER]: process.env.REACT_APP_FEATURE_NEW_COUNTER === 'true',
}

const storageKey = (flag) => `feature:${flag}`

/**
 * @param {string} flag one of FLAGS
 * @returns {boolean}
 */
export function isEnabled(flag) {
    const override = getItem(storageKey(flag), null)
    // Only a real boolean counts. A corrupt or hand-edited value falls through
    // to the build default rather than being coerced — `Boolean('false')` is
    // true, and that is precisely the mistake this guards against.
    if (override === true || override === false) return override
    return Boolean(BUILD_DEFAULTS[flag])
}

/**
 * Records a deliberate choice for this user, in this shop.
 * @param {string} flag
 * @param {boolean} enabled
 */
export function setEnabled(flag, enabled) {
    return setItem(storageKey(flag), Boolean(enabled))
}

/**
 * Forgets the choice and returns to the build default. Distinct from
 * setEnabled(flag, false) — see the note above.
 */
export function clearOverride(flag) {
    return removeItem(storageKey(flag))
}

/**
 * True when this user has expressed a preference either way. The switch uses it
 * to decide whether it is showing a choice or a default.
 */
export function hasOverride(flag) {
    const override = getItem(storageKey(flag), null)
    return override === true || override === false
}

/** Exposed for tests, so they assert against the same source the app reads. */
export function buildDefault(flag) {
    return Boolean(BUILD_DEFAULTS[flag])
}
